/**
 * src/components/Integrations/InstallLookerWizard.jsx
 *
 * A two-step wizard:
 *  1) Select forms
 *  2) For each selected form, fetch first submission -> parse fields
 *     -> sort them alphabetically -> let user pick columns.
 *
 * Hides critical columns ("organizationId", "formId", "submissionDocId")
 * so the user can't unselect them in the UI.
 * 
 * Updated to:
 *  - On mount (when open), fetch the user's existing "picks" from 
 *    /organizations/{orgId}/lookerSettings/picks
 *  - Prefill selectedForms and columnsState so they see their previous setup.
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  FormControlLabel,
  Checkbox,
  Grid
} from "@mui/material";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { getFormsForUser, getFormSubmissions } from "../../utils/firestore";
import { useAuth } from "../../contexts/AuthContext";
import { useOrganization2 } from "../../contexts/OrganizationContext2";

// We'll define which keys to ignore in the UI:
const CRITICAL_KEYS = new Set([
  "organizationId",
  "formId",
  "submissionId",
  "submissionDocId"
]);

/**
 * parseColumnsFromSubmission:
 * - Reads submission.responses
 * - Ignores critical keys & label_... entries
 * - Sorts remaining columns by label A-Z
 */
function parseColumnsFromSubmission(sub) {
  const responses = sub.responses || {};
  const cols = [];

  for (const key of Object.keys(responses)) {
    // Skip "label_" fields & critical columns
    if (key.startsWith("label_") || CRITICAL_KEYS.has(key)) {
      continue;
    }

    const labelKey = `label_${key}`;
    const labelVal = responses[labelKey] || key;

    cols.push({
      id: key,
      label: labelVal,
      visible: true
    });
  }

  // sort A–Z by label
  cols.sort((a, b) => a.label.localeCompare(b.label));
  return cols;
}

export default function InstallLookerWizard({
  open,
  onClose,
  onComplete
}) {
  const steps = ["Select Forms", "Pick Columns"];
  const [activeStep, setActiveStep] = useState(0);

  // Step 1: forms
  const [allForms, setAllForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedForms, setSelectedForms] = useState([]);

  // Step 2: columnsState[formId] = [ {id,label,visible} ]
  // final picks in selectedFields[formId]
  const [columnsState, setColumnsState] = useState({});
  const [selectedFields, setSelectedFields] = useState({});

  const { currentUser } = useAuth();
  const { organization } = useOrganization2();

  /**
   * On open, fetch the user's forms and attempt
   * to load their existing picks from /lookerSettings/picks.
   */
useEffect(() => {
  async function loadWizardData() {
    if (!open) return;

    setErrorMsg("");
    setAllForms([]);
    setSelectedForms([]);
    setColumnsState({});
    setSelectedFields({});
    setActiveStep(0);

    if (!currentUser?.uid || !organization?.id) {
      setErrorMsg("No user or organization found.");
      setLoadingForms(false);
      return;
    }

    try {
      // Start loading
      setLoadingForms(true);

      // 1) Fetch all forms available to this user
      const result = await getFormsForUser(currentUser.uid, organization.id);
      if (result.status === "success") {
        setAllForms(result.forms || []);
      } else {
        setErrorMsg(result.message || "Error fetching forms.");
        setAllForms([]);
      }

      // 2) Fetch previous Looker wizard selections (picks)
      const picksRef = doc(
        db,
        "organizations",
        organization.id,
        "lookerSettings",
        "picks"
      );
      const snap = await getDoc(picksRef);
      if (snap.exists()) {
        const data = snap.data();
        const rawSelectedForms = data.selectedForms || [];
        const rawSelectedFields = data.selectedFields || {};

        const validSelectedForms = [];
        const validSelectedFields = {};

        for (const formId of rawSelectedForms) {
          try {
            const formSnap = await getDoc(doc(db, "forms", formId));
            if (formSnap.exists()) {
              validSelectedForms.push(formId);
              validSelectedFields[formId] = rawSelectedFields[formId] || [];
            } else {
              console.warn(`[InstallLookerWizard] Skipping stale formId: ${formId} — does not exist in Firestore.`);
            }
          } catch (innerErr) {
            console.error(`[InstallLookerWizard] Error checking formId ${formId}:`, innerErr);
          }
        }

        setSelectedForms(validSelectedForms);
        setSelectedFields(validSelectedFields);

        console.log("[InstallLookerWizard] Loaded and validated selectedForms:", validSelectedForms);
      }
    } catch (err) {
      console.error("[InstallLookerWizard] Error loading wizard data:", err);
      setErrorMsg("An unexpected error occurred while loading Looker setup.");
    } finally {
      // ✅ Always stop the loading spinner, even on errors
      setLoadingForms(false);
    }
  }

  loadWizardData();
}, [open, currentUser, organization]);


  /**
   * Step 1 toggling forms on/off
   */
  const handleToggleForm = (formId, checked) => {
    if (checked) {
      setSelectedForms((prev) => [...prev, formId]);
    } else {
      setSelectedForms((prev) => prev.filter((id) => id !== formId));
      // Also remove from columnsState & selectedFields
      setColumnsState((prev) => {
        const { [formId]: _, ...rest } = prev;
        return rest;
      });
      setSelectedFields((prev) => {
        const { [formId]: __, ...rest } = prev;
        return rest;
      });
    }
  };

  /**
   * Step 2 toggling columns
   */
  const handleToggleColumn = (formId, colId, checked) => {
    setColumnsState((prev) => {
      const cols = prev[formId] || [];
      const updated = cols.map((col) =>
        col.id === colId ? { ...col, visible: checked } : col
      );
      return { ...prev, [formId]: updated };
    });
  };

  /**
   * Step 2 apply columns:
   * Store visible columns in selectedFields[formId] as objects
   * with { id, label } rather than just the raw ID.
   */
  const handleApplyColumns = (formId) => {
    const cols = columnsState[formId] || [];
    // Instead of storing only col.id, we now store { id, label }:
    const pickedCols = cols
      .filter((c) => c.visible)
      .map((c) => ({ id: c.id, label: c.label }));

    setSelectedFields((prev) => ({
      ...prev,
      [formId]: pickedCols
    }));
  };

  /**
   * Step 1 -> Step 2:
   * fetch the first submission for each selected form,
   * parse columns & store in columnsState.
   *
   * If we already have selectedFields for that form, mark
   * those columns as visible from the start so the user sees
   * their previous picks pre-checked.
   */
  const handleProceedToStep2 = async () => {
    for (const formId of selectedForms) {
      // If we haven't yet fetched columns for this form...
      if (!columnsState[formId]) {
        const resp = await getFormSubmissions(formId, null, 1);
        if (resp.status === "success" && resp.submissions.length > 0) {
          const firstSub = resp.submissions[0];
          let parsedCols = parseColumnsFromSubmission(firstSub);

          // If we have existing picks in selectedFields[formId],
          // mark those columns as visible, and mark others as false
          const existingPicks = selectedFields[formId] || [];
          if (existingPicks.length > 0) {
            parsedCols = parsedCols.map((col) => {
              // if the col.id is in existing picks, col.visible = true
              const isPicked = existingPicks.some(p => p.id === col.id);
              return { ...col, visible: isPicked };
            });
          }

          setColumnsState((prev) => ({
            ...prev,
            [formId]: parsedCols
          }));
        } else {
          console.warn(`No data for form: ${formId}`);
          setColumnsState((prev) => ({
            ...prev,
            [formId]: []
          }));
        }
      } else {
        // else we do nothing, we already have columns
        // from previous fetch or picks
      }
    }
    setActiveStep(1);
  };

  /**
   * Next/Finish button
   */
  const handleNext = async () => {
    if (activeStep === 0) {
      // from Step 1 to Step 2
      await handleProceedToStep2();
    } else {
      // finishing wizard
      const result = { selectedForms, selectedFields };
      if (onComplete) onComplete(result);
      onClose();
    }
  };

  /**
   * Back/Cancel button
   */
  const handleBack = () => {
    if (activeStep === 0) {
      onClose();
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const isStep1 = activeStep === 0;
  const isStep2 = activeStep === 1;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Install Looker Integration</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* STEP 1: SELECT FORMS */}
        {isStep1 && (
          <div>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Step 1: Select which forms to connect with Looker.
            </Typography>

            {loadingForms && (
              <Typography>Loading forms...</Typography>
            )}
            {!loadingForms && errorMsg && (
              <Typography color="error">
                {errorMsg}
              </Typography>
            )}
            {!loadingForms && !errorMsg && allForms.length === 0 && (
              <Typography>No forms found.</Typography>
            )}
            {!loadingForms && !errorMsg && allForms.length > 0 && (
              <>
                {allForms.map((form) => {
                  const checked = selectedForms.includes(form.id);
                  return (
                    <FormControlLabel
                      key={form.id}
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={(e) =>
                            handleToggleForm(form.id, e.target.checked)
                          }
                        />
                      }
                      label={form.title || form.id}
                    />
                  );
                })}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  After selecting forms, click "Next" to load
                  their fields from the first submission.
                </Typography>
              </>
            )}
          </div>
        )}

        {/* STEP 2: PICK COLUMNS (alphabetical) + responsive grid */}
        {isStep2 && (
          <div>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Step 2: Pick columns for each selected form
              (alphabetical order, responsive grid).
            </Typography>

            {selectedForms.length === 0 && (
              <Typography>
                No forms selected. Go Back to choose forms first.
              </Typography>
            )}

            {selectedForms.map((formId) => {
              const cols = columnsState[formId] || [];

              return (
                <div key={formId} style={{ marginBottom: "1rem" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Columns for {formId}:
                  </Typography>

                  {cols.length === 0 && (
                    <Typography color="text.secondary">
                      No submissions or no fields found.
                    </Typography>
                  )}

                  <Grid container spacing={1}>
                    {cols.map((col) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                        lg={2}
                        key={col.id}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={col.visible}
                              onChange={(e) =>
                                handleToggleColumn(
                                  formId,
                                  col.id,
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label={col.label}
                        />
                      </Grid>
                    ))}
                  </Grid>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleApplyColumns(formId)}
                    sx={{ mt: 1 }}
                  >
                    Apply
                  </Button>

                  {selectedFields[formId] && (
                    <Typography
                      variant="body2"
                      sx={{ ml: 2, mt: 1 }}
                    >
                      Selected Field IDs:{" "}
                      {JSON.stringify(selectedFields[formId])}
                    </Typography>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleBack}>
          {isStep1 ? "Cancel" : "Back"}
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={
            isStep1 &&
            !loadingForms &&
            allForms.length > 0 &&
            selectedForms.length === 0
          }
        >
          {isStep2 ? "Finish" : "Next"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
